// ── Preferencias de interfaz, del servidor y por usuario ─────────────────────
//
// El menú lateral no se guardaba en ningún lado y el panel de Evo Brain vivía en
// `localStorage`. Ahora la fuente de verdad es el servidor, para que el estado
// viaje entre sesiones y entre máquinas.
//
// `localStorage` NO se va: queda como eco local para el primer pintado. El valor
// del servidor llega asincrónico, y sin eco la interfaz se dibujaría abierta y
// se cerraría de golpe medio segundo después. Ese parpadeo se ve peor que no
// recordar nada.
import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

// Las mismas que acepta el servidor (services/uiPrefs.ts CLAVES_UI). Si allá
// se agrega una, acá también, o el tipo la rechaza antes de salir.
export type ClaveUi =
  | 'sidebar.collapsed' | 'assistant.collapsed' | 'aviso.sonido' | 'aviso.voz'
  // "Ya vi el recorrido de este módulo". Es una familia y no una constante por
  // módulo; el servidor valida el módulo contra la lista real, así que
  // `recorrido.cualquier-cosa` se rechaza antes de escribir.
  | `recorrido.${string}`
  // El panel de ayuda, abierto o cerrado. Mismo criterio que el asistente:
  // viaja entre máquinas.
  | 'ayuda.abierta';

const ecoKey = (clave: ClaveUi) => `evo-ui-${clave}`;

function leerEco(clave: ClaveUi): string | null {
  try { return localStorage.getItem(ecoKey(clave)); } catch { return null; }
}

function escribirEco(clave: ClaveUi, valor: string): void {
  try { localStorage.setItem(ecoKey(clave), valor); } catch { /* sin almacenamiento */ }
}

export function useUiPrefs() {
  return useQuery<Record<string, string>>({
    queryKey: ['ui-prefs'],
    queryFn: () => api.get<Record<string, string>>('/api/ui-prefs'),
    // No cambia sola: la escribe esta misma persona desde alguna máquina.
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * Una bandera de interfaz, con el servidor como fuente de verdad.
 *
 * Devuelve `[valor, cambiar]`. El cambio se ve al instante y se persiste en
 * paralelo: esperar la respuesta para mover el panel se siente como que el clic
 * no hizo nada.
 */
export function useUiFlag(clave: ClaveUi, porDefecto = false): [boolean, (v: boolean) => void] {
  const qc = useQueryClient();
  const { data } = useUiPrefs();
  const [local, setLocal] = useState<boolean>(() => {
    const eco = leerEco(clave);
    return eco === null ? porDefecto : eco === 'true';
  });

  // Cuando llega el servidor, manda él — es lo que hace que el estado viaje
  // entre máquinas.
  useEffect(() => {
    const remoto = data?.[clave];
    if (remoto === undefined) return;
    const v = remoto === 'true';
    setLocal(v);
    escribirEco(clave, String(v));
  }, [data, clave]);

  const guardar = useMutation({
    mutationFn: (value: string) => api.put(`/api/ui-prefs/${clave}`, { value }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['ui-prefs'] }); },
  });

  const cambiar = useCallback((v: boolean) => {
    setLocal(v);
    escribirEco(clave, String(v));
    guardar.mutate(String(v));
  // `guardar` es estable entre renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);

  return [local, cambiar];
}
